import React, { useState } from 'react';
import { Dropdown, Form, Button } from 'react-bootstrap';
import { Check } from 'lucide-react';
import { BaseType } from '../types/Enums';
import { translate } from '../types/EnumTranslations';
import { RELEVANT_BASE_TYPES } from '../Helpers/baseTypeUtils';
import './BaseTypeMultiSelect.css';

interface BaseTypeMultiSelectProps {
    selected: BaseType[];
    onChange: (baseTypes: BaseType[]) => void;
    disabled?: boolean;
}

/**
 * Multi-select dropdown for BaseTypes.
 * Used by the superadmin library pages (maintenance + lifetime).
 *
 * The menu stays open while ticking base types and is closed with the
 * "Klar" button (or the toggle), so several can be picked in one go.
 */
export const BaseTypeMultiSelect: React.FC<BaseTypeMultiSelectProps> = ({ selected, onChange, disabled }) => {
    const [open, setOpen] = useState(false);

    const toggleBaseType = (baseType: BaseType) => {
        if (selected.includes(baseType)) {
            onChange(selected.filter((bt) => bt !== baseType));
        } else {
            onChange([...selected, baseType]);
        }
    };

    const selectedCount = selected.length;
    const label = selectedCount === 0
        ? 'Välj bastyper...'
        : selectedCount === 1
            ? translate(selected[0])
            : `${selectedCount} bastyper valda`;

    return (
        <Dropdown
            className="base-type-multi-select"
            show={open}
            onToggle={(nextShow) => setOpen(nextShow)}
            autoClose={false}
        >
            <Dropdown.Toggle
                variant="outline-secondary"
                className="w-100 text-start d-flex justify-content-between align-items-center"
                disabled={disabled}
            >
                <span className="text-truncate">{label}</span>
            </Dropdown.Toggle>
            <Dropdown.Menu className="w-100">
                <div className="base-type-multi-select-list">
                    {RELEVANT_BASE_TYPES.map((baseType) => (
                        <Dropdown.Item
                            key={baseType}
                            as="button"
                            onClick={(e) => {
                                e.preventDefault();
                                toggleBaseType(baseType);
                            }}
                            active={selected.includes(baseType)}
                        >
                            {/* pe-none lets clicks on the checkbox fall through to the
                                Dropdown.Item button, which owns the toggle. */}
                            <Form.Check
                                type="checkbox"
                                label={translate(baseType)}
                                checked={selected.includes(baseType)}
                                readOnly
                                tabIndex={-1}
                                className="pe-none"
                            />
                        </Dropdown.Item>
                    ))}
                </div>
                <div className="base-type-multi-select-footer">
                    <span className="text-muted small">
                        {selectedCount === 0 ? 'Inga bastyper valda' : `${selectedCount} valda`}
                    </span>
                    <Button variant="primary" size="sm" onClick={() => setOpen(false)}>
                        <Check size={14} className="me-1" />
                        Klar
                    </Button>
                </div>
            </Dropdown.Menu>
        </Dropdown>
    );
};

export default BaseTypeMultiSelect;
